import { stripLegacyValidationProfiles } from './url-answer-validation';
import type { FormField } from '../types';
import type { ConversationExperienceConfig } from './multimodal-contract';
import { normalizeConversationSections } from './conversation-sections';

/** Inputs from an existing definition or an unsaved creator draft. */
export interface ConversationDefinitionInput {
  fields?: readonly FormField[] | null;
  config?: ConversationExperienceConfig | null;
  details?: string;
  goal?: string;
  requiredVideo?: boolean;
}

/** Assigns collision-free names without changing already valid answer keys. */
function canonicalName(raw: string, reserved: Set<string>, used: Set<string>, preserveExplicit: boolean): string {
  if (preserveExplicit && !used.has(raw)) {
    used.add(raw);
    return raw;
  }
  let base = raw.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'question';
  if (!/^[a-z]/.test(base)) base = `question_${base}`;
  base = base.slice(0, 200);
  let name = base;
  let suffix = 2;
  while (reserved.has(name) || used.has(name)) {
    const ending = `_${suffix++}`;
    name = `${base.slice(0, 200 - ending.length)}${ending}`;
  }
  used.add(name);
  return name;
}

/**
 * Converts stored definitions to the single authoring format. This is not a
 * publish validator: missing resources remain editable and are never replaced.
 * Historical response snapshots are deliberately outside this function.
 */
export function normalizeConversationDefinition(input: ConversationDefinitionInput): { fields: FormField[]; conversationConfig: ConversationExperienceConfig } {
  let fields = structuredClone([...(input.fields ?? [])]).map(stripLegacyValidationProfiles);
  if (fields.length === 0) {
    const source = input.details?.trim() || input.goal?.trim() || '';
    fields = source.split(/[;\n]+/).map(topic => topic.trim()).filter(Boolean).map((topic, index) => ({
      id: `migrated_topic_${index + 1}`, name: `migrated_topic_${index + 1}`,
      type: 'free_form_explore', label: topic.slice(0, 1000), topic,
      required: false, mustOrTry: 'try',
    }));
  }
  const sourceIds = new Set(fields.map(field => field.id));
  const reserved = new Set(fields.map(field => field.name).filter(name => /^[a-z][a-z0-9_]{0,199}$/.test(name)));
  const ids = new Set(sourceIds);
  const converted: FormField[] = [];
  for (const field of fields) {
    if (field.type !== 'information' || field.responseMode === 'passive') {
      converted.push(field);
      continue;
    }
    const child = fields.find(candidate => candidate.informationFieldId === field.id);
    converted.push({ ...field, contentVariant: field.contentVariant ?? 'image', responseMode: 'passive', required: false });
    if (child) continue;
    let id = `${field.id.slice(0, 185)}_reaction`;
    let suffix = 2;
    while (ids.has(id)) id = `${field.id.slice(0, 180)}_reaction_${suffix++}`;
    ids.add(id);
    converted.push({
      id, name: `${field.name}_reaction`, label: 'What do you think about this?',
      type: 'free_form_explore', required: field.required, mustOrTry: field.mustOrTry,
      informationFieldId: field.id, ...(field.directive ? { topic: field.directive } : {}),
    });
  }
  const children = new Map<string, FormField>();
  for (const field of converted) {
    if (field.type !== 'information') continue;
    const child = converted.find(candidate => candidate.informationFieldId === field.id
      && candidate.type !== 'information' && candidate.type !== 'calendar_booking');
    if (child) children.set(field.id, child);
  }
  const childIds = new Set([...children.values()].map(field => field.id));
  const grouped: FormField[] = [];
  for (const field of converted) {
    if (childIds.has(field.id)) continue;
    // Older drafts could attach multiple questions. Preserve every question,
    // but only the first belongs to the Information/question group.
    delete field.informationFieldId;
    grouped.push(field);
    const child = children.get(field.id);
    if (child) grouped.push(child);
  }
  const used = new Set<string>();
  const normalized = grouped.map(field => ({
    ...field,
    name: canonicalName(field.name.trim() ? field.name : field.label, reserved, used, sourceIds.has(field.id) && /^[a-z][a-z0-9_]{0,199}$/.test(field.name)),
  }));
  const conversationConfig = structuredClone(input.config ?? { version: 1 as const, callsEnabled: true });
  if (conversationConfig.sections) conversationConfig.sections = normalizeConversationSections(normalized, conversationConfig.sections);
  return { fields: normalized, conversationConfig };
}
